// src/services/s3MetadataReader.service.ts - Enhanced with session management
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { SessionMetadataFile, SessionImage, CredentialsResponse } from '../types/upload.types';
import {  S3_CONFIG, DEBUG_CONFIG } from '../constants/upload.constants';
import { s3CredentialsManager } from './s3CredentialsManager.service';

export interface ReconstructionImageData {
  image_key: string;
  timestamp: string;
  image_width_px: number;
  image_height_px: number;
  focal_length_35mm?: number;
}

export interface ParsedSessionMetadata {
  sessionUUID: string;
  destinationFolder: string;
  sequences: ReconstructionImageData[][];  //  Multi-camera sequences   
  preset?: string;                           // Capture preset
  options?: Record<string, any>;            //  Options object
  blurStatistics: {
    blurryImages: number;
    blurryPercentage: number;
    totalImages: number;
  };
}

export class S3MetadataReaderService {
  private s3Client: S3Client | null = null;
  private activeFolderPath: string | null = null; // Track active session

  /**
   * Initialize S3 client with credentials from session manager
   */
  private async initializeS3Client(folderPath: string): Promise<void> {
    try {
      if (DEBUG_CONFIG.logS3Operations) {
        console.log(`[S3MetadataReader] 🚀 Initializing S3 client with session management...`);
        console.log(`[S3MetadataReader] Folder Path: ${folderPath}`);
      }

      // ===== ENHANCED: Use thumbnail credentials manager =====
      console.log(`[S3MetadataReader] 📡 Getting thumbnail credentials via session manager...`);
      
      const credentialsResponse = await s3CredentialsManager.getThumbnailCredentials(folderPath);
      
      if (!credentialsResponse.success || !credentialsResponse.data) {
        throw new Error('Failed to get thumbnail credentials from session manager');
      }

      const credentials = credentialsResponse.data;
      this.activeFolderPath = folderPath; // Track for cleanup

      if (DEBUG_CONFIG.logS3Operations) {
        console.log(`[S3MetadataReader] ✅ Got credentials via session manager for region: ${credentials.region}`);
        console.log(`[S3MetadataReader] Credentials AccessKeyId: ${credentials.credentials.AccessKeyId?.substring(0, 10)}...`);
      }

      this.s3Client = new S3Client({
        region: credentials.region,
        credentials: {
          accessKeyId: credentials.credentials.AccessKeyId,
          secretAccessKey: credentials.credentials.SecretAccessKey,
          sessionToken: credentials.credentials.SessionToken
        }
      });

      if (DEBUG_CONFIG.logS3Operations) {
        console.log(`[S3MetadataReader] ✅ S3 client initialized successfully with session management`);
      }

    } catch (error) {
      console.error(`[S3MetadataReader] Failed to initialize S3 client with session management:`, error);
      throw new Error(`Failed to initialize S3 client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Initialize S3 client with existing credentials (reuse upload credentials)
   */
  private initializeS3ClientWithCredentials(credentials: CredentialsResponse): void {
    try {
      if (DEBUG_CONFIG.logS3Operations) {
        console.log(`[S3MetadataReader] 🔄 Initializing S3 client with existing upload credentials...`);
        console.log(`[S3MetadataReader] Region: ${credentials.region}`);
        console.log(`[S3MetadataReader] Bucket: ${credentials.bucket}`);
      }

      this.s3Client = new S3Client({
        region: credentials.region,
        credentials: {
          accessKeyId: credentials.credentials.AccessKeyId,
          secretAccessKey: credentials.credentials.SecretAccessKey,
          sessionToken: credentials.credentials.SessionToken
        }
      });

      // Don't track folder path since we're reusing upload credentials
      this.activeFolderPath = null;

      if (DEBUG_CONFIG.logS3Operations) {
        console.log(`[S3MetadataReader] ✅ S3 client initialized with existing upload credentials`);
      }

    } catch (error) {
      console.error(`[S3MetadataReader] Failed to initialize S3 client with existing credentials:`, error);
      throw new Error(`Failed to initialize S3 client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }


  /**
   * Cleanup method to complete thumbnail session
   */
  private async cleanup(): Promise<void> {
    if (this.activeFolderPath) {
      try {
        console.log(`[S3MetadataReader] 🧹 Completing thumbnail session for: ${this.activeFolderPath}`);
        await s3CredentialsManager.completeThumbnailSession(this.activeFolderPath);
        this.activeFolderPath = null;
      } catch (error) {
        console.warn(`[S3MetadataReader] Failed to complete thumbnail session:`, error);
      }
    }
    this.s3Client = null;
  }

  /**
   * Read sessionMetadata.json from S3 and parse it (Enhanced with session management)
   */

  async readSessionMetadata(
    folderPath: string, 
    bucket?: string,
    existingCredentials?: CredentialsResponse
  ): Promise<ParsedSessionMetadata> {
    try {
      console.log(`[S3MetadataReader] Reading sessionMetadata.json from: ${folderPath} (with session management)`);

      // Initialize S3 client if not already done or if we have existing credentials
      if (!this.s3Client || existingCredentials) {
        if (existingCredentials) {
          console.log(`[S3MetadataReader] Using existing upload credentials`);
          this.initializeS3ClientWithCredentials(existingCredentials);
        } else {
          await this.initializeS3Client(folderPath);
        }
      }

      if (!this.s3Client) {
        throw new Error('Failed to initialize S3 client');
      }

      // Construct the S3 key for sessionMetadata.json
      const metadataKey = `${folderPath}/sessionMetadata.json`;
      
      // Use default bucket if not provided
      const s3Bucket = bucket || S3_CONFIG.bucket || 'ar-trial';

      console.log(`[S3MetadataReader] 📡 Fetching from bucket: ${s3Bucket}, key: ${metadataKey}`);

      // Get the object from S3
      const command = new GetObjectCommand({
        Bucket: s3Bucket,
        Key: metadataKey
      });

      const response = await this.s3Client.send(command);

      if (!response.Body) {
        throw new Error('No content found in sessionMetadata.json');
      }

      // Convert stream to string
      const chunks: Uint8Array[] = [];
      const reader = response.Body.transformToWebStream().getReader();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }

      const concatenated = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0));
      let offset = 0;
      for (const chunk of chunks) {
        concatenated.set(chunk, offset);
        offset += chunk.length;
      }

      const jsonString = new TextDecoder().decode(concatenated);
      console.log(`[S3MetadataReader] 📄 Raw sessionMetadata.json content retrieved`);

      // Parse JSON directly 
      const sessionMetadataFile: SessionMetadataFile = JSON.parse(jsonString);

      // Validate the structure
      if (!sessionMetadataFile.sequence_set_uuid || !Array.isArray(sessionMetadataFile.sequences)) {
        throw new Error('Invalid sessionMetadata structure: missing sequence_set_uuid or sequences');
      }

      const sequences = sessionMetadataFile.sequences;
      const totalImages = sequences.reduce((sum, seq) => sum + seq.length, 0);
    
      
    console.log(`[S3MetadataReader] 📹 Multi-camera data:`, {
        sequenceCount: sequences.length,
        totalImages: totalImages,
        imagesPerSequence: sequences.map(seq => seq.length)
      });

      sequences.forEach((seq, idx) => {
        console.log(`[S3MetadataReader]   Camera ${idx + 1}: ${seq.length} images`);
      });

      // Convert to reconstruction format
      const reconstructionSequences = sequences.map(sequence => 
        sequence.map((image: SessionImage) => ({
          image_key: image.image_key,
          timestamp: image.timestamp,
          image_width_px: image.image_width_px,
          image_height_px: image.image_height_px,
          ...(image.focal_length_35mm !== undefined && image.focal_length_35mm !== null && {
            focal_length_35mm: image.focal_length_35mm
          })
        }))
      );

      
      const result: ParsedSessionMetadata = {
        sessionUUID: sessionMetadataFile.sequence_set_uuid,
        destinationFolder: sessionMetadataFile.destinationFolder,
        sequences: reconstructionSequences,
        preset: sessionMetadataFile.preset,
        options: sessionMetadataFile.options || {},
        blurStatistics: sessionMetadataFile.blurStatistics
      };


      console.log(`[S3MetadataReader] ✅ Successfully parsed sessionMetadata:`, {
        sessionUUID: result.sessionUUID,
        destinationFolder: result.destinationFolder,
        sequenceCount: result.sequences?.length || 0,
        totalImages: totalImages,
        preset: result.preset
      });

      console.log(`[S3MetadataReader] sample image paths from each camera:`);
      result.sequences.forEach((seq, idx) => {
        if (seq.length > 0) {
          console.log(`  Camera ${idx + 1}: ${seq[0].image_key}`);
        }
      });

      return result;

    } catch (error) {
      console.error(`[S3MetadataReader]  Failed to read sessionMetadata:`, error);
      throw new Error(`Failed to read session metadata: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Auto-cleanup unless using existing credentials
      if (!existingCredentials && this.activeFolderPath) {
        setTimeout(() => {
          this.cleanup().catch(err => 
            console.warn('[S3MetadataReader] Cleanup failed:', err)
          );
        }, 30000); // 30 second delay
      }
    }
  }


  /**
   * Validate that the image paths in metadata match expected S3 structure
   */
  validateImagePaths(metadata: ParsedSessionMetadata): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    try {
      // Flatten sequences for validation
      const allImages = metadata.sequences.flat();
      
      // Check that all images have proper paths
      allImages.forEach((image, index) => {
        if (!image.image_key || !image.image_key.includes('/')) {
          errors.push(`Image ${index + 1}: Invalid image_key format: ${image.image_key}`);
        }

        if (!image.timestamp || isNaN(parseInt(image.timestamp))) {
          errors.push(`Image ${index + 1}: Invalid timestamp: ${image.timestamp}`);
        }

        if (image.image_width_px <= 0 || image.image_height_px <= 0) {
          errors.push(`Image ${index + 1}: Invalid dimensions: ${image.image_width_px}x${image.image_height_px}`);
        }

        if (image.focal_length_35mm !== undefined && image.focal_length_35mm <= 0) {
          errors.push(`Image ${index + 1}: Invalid focal length: ${image.focal_length_35mm}`);
        }
      });

      // Check that destination folder matches image paths
      const firstImage = metadata.sequences[0]?.[0];
      if (firstImage) {
        // Normalize paths (remove trailing slashes)
        const normalizedDestination = metadata.destinationFolder.replace(/\/$/, '');
        
        // Extract folder from image path (everything except filename)
        const folderFromImages = firstImage.image_key.split('/').slice(0, -1).join('/');
        
        // For multi-camera: images can be in subfolders like camera1/, camera2/
        // Check if image folder STARTS WITH destination folder (allows subfolders)
        if (folderFromImages && !folderFromImages.startsWith(normalizedDestination)) {
          console.warn(
            `[S3MetadataReader] Folder mismatch - ` +
            `expected images under '${normalizedDestination}', ` +
            `but found '${folderFromImages}'`
          );
        }
      }

    } catch (error) {
      errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Read and validate sessionMetadata for reconstruction (Enhanced with session management)
   */
  async readAndValidateForReconstruction(
    folderPath: string,
    existingCredentials?: CredentialsResponse
  ): Promise<ParsedSessionMetadata> {
    try {
      console.log(`[S3MetadataReader] 🔍 Reading and validating metadata for reconstruction`);
      
      const metadata = await this.readSessionMetadata(folderPath, undefined, existingCredentials);
      
      const validation = this.validateImagePaths(metadata);
      if (!validation.isValid) {
        console.warn(`[S3MetadataReader] ⚠️ Validation warnings:`, validation.errors);
        // Continue despite warnings
      }

      console.log(`[S3MetadataReader] ✅ Metadata read and validated successfully`);
      return metadata;

    } catch (error) {
      console.error(`[S3MetadataReader] Failed to read and validate metadata:`, error);
      throw error;
    }
  }

  /**
   * Manual cleanup method
   */
  async manualCleanup(): Promise<void> {
    await this.cleanup();
  }

  /**
   * Get current session status (for debugging)
   */
  getSessionStatus(): { hasActiveSession: boolean; folderPath: string | null } {
    return {
      hasActiveSession: !!this.activeFolderPath,
      folderPath: this.activeFolderPath
    };
  }
}

// Export singleton instance
export const s3MetadataReader = new S3MetadataReaderService();